const CAP_LOS_NODOS = {
  id: 130,
  title: "Los Nodos",
  slug: "los-nodos",
  type: "lesson",
  category: "bitcoin-basico",
  level: "basic",
  preview:
    "Los nodos son los ordenadores conectados a la red Bitcoin que almacenan, validan y distribuyen información, garantizando el funcionamiento, la seguridad y la descentralización del sistema.",
  sections: [
    {
      heading: "¿Qué es un nodo?",
      text: [
        "En informática, un **nodo** es cualquier punto de conexión que puede **crear, recibir, almacenar y enviar datos**. Puede ser un ordenador, un servidor o incluso un dispositivo especializado conectado a una red.",
        "En el contexto de **Bitcoin y blockchain**, los nodos son **ordenadores que ejecutan el software de Bitcoin** para permitir que la red funcione. Sin nodos, Bitcoin simplemente no existiría.",
        "Estos nodos están conectados mediante una **red peer-to-peer (P2P)**, lo que significa que **no hay jerarquías ni centro de control**, todos los nodos son igualmente importantes."
      ],
      list: [
        "**Crean y transmiten información**",
        "**Validan transacciones y bloques**",
        "**Almacenan copia de la blockchain**",
        "**Contribuyen a la seguridad del sistema**"
      ],
      notes: [
        "Bitcoin fue la primera criptomoneda en utilizar miles de nodos distribuidos para funcionar sin una autoridad central."
      ],
      image: "nodos-red-general.png"
    },
    {
      heading: "¿Cómo funciona un nodo Bitcoin?",
      text: [
        "Para convertirse en nodo, **cualquier persona puede instalar Bitcoin Core** y unirse voluntariamente a la red. No hay permisos ni autorizaciones.",
        "Bitcoin funciona como una **gran red de nodos que se comunican entre sí**, compartiendo información sobre transacciones y bloques.",
        "El funcionamiento de la red comenzó cuando **Satoshi Nakamoto activó el primer nodo**. Luego Hal Finney corrió el segundo nodo, y desde allí se inició la expansión.",
        "Cada nuevo nodo que se conecta se sincroniza con otros nodos, copiando toda la blockchain y manteniéndola actualizada constantemente.",
        "Los nodos actúan como **guardianes del sistema**, ya que siguen y hacen cumplir las reglas del protocolo Bitcoin."
      ],
      bluepoints: [
        "Todos los nodos operan con las mismas reglas",
        "Todos transmiten y reciben información",
        "Cada nodo puede decidir si una transacción es válida"
      ],
      notes: ["No existe un nodo jefe ni central. Todos tienen la misma autoridad."]
    },
    {
      heading: "Funciones principales de los nodos",
      text: [
        "Los nodos cumplen varias funciones esenciales para la supervivencia de Bitcoin:",
        "Cada nodo puede realizar algunas o todas estas funciones, dependiendo del tipo de nodo y su configuración."
      ],
      list: [
        "**Almacenar una copia de la blockchain**",
        "**Validar transacciones** y verificar que sean legítimas",
        "**Transmitir información** a otros nodos",
        "**Detectar fraudes**, como el doble gasto",
        "**Evitar bloques inválidos**",
        "**Mantener el sistema operativo y disponible**"
      ],
      notes: [
        "Gracias a los nodos, los usuarios pueden usar Bitcoin sin confiar en bancos o empresas."
      ]
    },
    {
      heading: "Tipos de nodos en la red Bitcoin",
      text: [
        "En la red Bitcoin existen diferentes tipos de nodos. Todos son importantes, pero no todos realizan las mismas tareas."
      ],
      table: {
        columns: [
          "Tipo de nodo",
          "Descripción",
          "Funciones / Características",
          "Notas"
        ],
        rows: [
          {
            "Tipo de nodo": "Nodos Completos",
            Descripción:
              "Almacenan toda la blockchain, desde el bloque génesis hasta el último bloque. Hacen cumplir las reglas del protocolo Bitcoin y protegen la honestidad del sistema.",
            "Funciones / Características":
              "Validan transacciones; Detectan intentos de doble gasto; Actualizan la blockchain; Transmiten información a otros nodos; Verifican tamaño de bloque, firmas y que nadie gaste bitcoin que no tiene.",
            Notas:
              "El software más común para nodos completos es Bitcoin Core."
          },
          {
            "Tipo de nodo": "Supernodos (Nodos de Escucha)",
            Descripción:
              "Son nodos completos que están siempre conectados y disponibles públicamente.",
            "Funciones / Características":
              "Permiten que otros nodos se conecten sin permisos; Actúan como puntos de entrada a la red; Transmiten datos a varios nodos a la vez.",
            Notas:
              "Suelen estar configurados para alta disponibilidad y buena conexión de red."
          },
          {
            "Tipo de nodo": "Nodos de Minería",
            Descripción:
              "Nodos que almacenan la blockchain y además ejecutan software de minería.",
            "Funciones / Características":
              "Intentan resolver el problema matemático de Proof-of-Work; Generan nuevos bloques y reciben recompensas; Participan en la validación de transacciones y mantienen la red funcionando.",
            Notas:
              "Actualmente se utilizan equipos ASIC, FPGA o GPU para minar."
          },
          {
            "Tipo de nodo": "Nodos Ligeros",
            Descripción:
              "No descargan la blockchain completa, sino que dependen de los nodos completos.",
            "Funciones / Características":
              "Se usan en wallets de teléfonos y aplicaciones ligeras; Verifican si una transacción ha sido confirmada; No almacenan el historial completo de bloques.",
            Notas:
              "Ideales para dispositivos con poco almacenamiento o conexión limitada."
          }
        ]
      },
      image: "tipos_nodos_bitcoin.png"
    },
    {
      heading: "Seguridad y protección de la red Bitcoin",
      text: [
        "La red Bitcoin es extremadamente resistente, gracias a los nodos completos y supernodos que la mantienen viva.",
        "Cada vez que se valida un nuevo bloque, los nodos **lo almacenan, verifican y distribuyen** hacia otros nodos.",
        "Incluso si miles de nodos fallan, **mientras uno permanezca activo, la red puede ser reconstruida**.",
        "Esto es lo que hace que Bitcoin no dependa de un servidor central, ni pueda ser apagado fácilmente."
      ],
      notes: [
        "Cuantos más nodos haya en el mundo, más **segura y confiable** será la red Bitcoin.",
        "Los nodos son como copias de seguridad vivientes y activas del sistema."
      ],
      image: "nodo_seguridad_red.png"
    }
  ]
};

export default CAP_LOS_NODOS;
